import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { notification } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { CompanyEmployee, setPasswordForUser } from 'core'

import { Alert, Button, Modal, PasswordInput } from '../../../../../../common/components'
import { useCommandRequest } from '../../../../../../common/hooks'

type SetPasswordForUserModalProps = {
  user?: CompanyEmployee
  onCancel?: () => void
  onPasswordChanged?: () => void
}

const SetPasswordForUserModal: React.FC<SetPasswordForUserModalProps> = (props) => {
  const { t: translate } = useTranslation()

  const [initialValues] = useState<FormData>({
    newPassword: '',
    confirmPassword: '',
  })
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const submitData = async (values: FormData) => {
    const result = await sendCommand(() =>
      setPasswordForUser({
        userId: props.user.userId,
        newPassword: values.newPassword,
      }),
    )

    if (result.completed) {
      notification.open({
        message: `Password changed for ${props.user.fullName}`,
        type: 'success',
      })

      if (props.onPasswordChanged) {
        props.onPasswordChanged()
      }
    }
  }

  return (
    <Modal
      visible={!!props.user}
      title={`Set password for ${props.user?.fullName}`}
      onCancel={props.onCancel}
      footer={null}
    >
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={async (values: any, formik) => {
          await submitData(values)
          formik.resetForm()
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            {props.user && (
              <>
                <Alert
                  className={'mb-16'}
                  type={'info'}
                  message={`You are setting the password for ${props.user.fullName}.`}
                />

                <PasswordInput
                  label={translate('user-header.change-password-form.labels.password')}
                  name="newPassword"
                  setPassword={() => {}}
                />

                <PasswordInput
                  label={translate('user-header.change-password-form.labels.confirm-password')}
                  name="confirmPassword"
                  setPassword={() => {}}
                />

                <Button
                  htmlType="submit"
                  className={'w-100'}
                  type="primary"
                  loading={isCommandLoading}
                  disabled={!formValidator(translate).isValidSync(formik.values) || isCommandLoading}
                >
                  Set Password
                </Button>
              </>
            )}
          </form>
        )}
      </Formik>
    </Modal>
  )
}

type FormData = {
  newPassword: string
  confirmPassword: string
}

const formValidator = (translate: any) =>
  Yup.object({
    newPassword: Yup.string().required(translate('general.required')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], translate('user-header.change-password-form.errors.confirm-password'))
      .required(translate('general.required')),
  })

export default SetPasswordForUserModal
